import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type RefObject } from "react";
import type { ReadingLayout } from "odyssey-shared";
import { Button } from "./Button.tsx";
import { useCentred } from "./measure.ts";
import { KIT_METRICS, logical } from "./metrics.ts";
import { pageGeometry, pageTop, scrollFor, turnPage, type PageGeometry } from "./reading-pages.ts";

/**
 * The reading panel (P1.9-T37, ADR-061's amendment "the reading panel", ADR-077): the window frame holding a text
 * the player reads and acknowledges, one size for every text (`KIT_METRICS.reading`), with a primary button that
 * closes it. It is a dialog that is not modal: no scrim, no hold on the keyboard and no focus trap, so the world
 * behind it stays live, and it catches the pointer only over its own box. It has no title in view; `label` names it
 * for assistive technology, and the whole text describes it, on every page, so a screen reader announces the dialog
 * with its text when focus enters it. The text is set as a text node, never as markup, with its line breaks kept and
 * any run too long for a line broken where it must be.
 *
 * A text taller than the panel reads by its `layout`:
 * - `scroll`: it scrolls inside the panel with the kit's scroll bar, and it is in the tab order before the button.
 * - `pages`: it breaks on whole lines into pages as tall as the whole lines that fit, and the footer gains Back, the
 *   page counter (a polite live region) and Next before Close. Back is disabled on the first page and Next on the
 *   last; when one is disabled under focus, focus moves to Close. A text that fits one page shows no page controls.
 * A new `serial` starts the text again from its top or its first page. `navigate` hands the page a function that
 * answers Page Down, Page Up, Home and End (`reading-pages.ts`): it turns the pages, or scrolls the text.
 *
 * Which keys reach `navigate`, which close the panel, what takes focus and where focus returns are the page's
 * (`game/reading/`), since a page decides how the panel shares the keyboard with what lies behind it.
 *
 * @param props The `text` and its `layout`; the `serial` of the reading; the `label` naming the dialog and the
 *   `words` of its controls, copy the page owns; `onClose`, which Close calls; `panel`, a ref the page may read the
 *   dialog's element through; `textRef`, one for the text's box; `navigate`, a ref the panel fills with its key
 *   answer.
 * @returns The area holding the panel.
 * @example
 * ```tsx
 * <ReadingPanel text={sign} layout="pages" serial={1} label="Sign" words={words} onClose={close} navigate={keys} />
 * ```
 */
export function ReadingPanel({ text, layout = "scroll", serial = 0, label, words, onClose, panel, textRef, navigate }: ReadingPanelProps): ReactElement {
  const textId = useId();
  const area = useRef<HTMLDivElement>(null);
  const own = useRef<HTMLDivElement>(null);
  const ownText = useRef<HTMLDivElement>(null);
  const lines = useRef<HTMLDivElement>(null);
  const box = panel ?? own;
  const textBox = textRef ?? ownText;
  const place = useCentred(area, box, { x: true, y: true }, null);
  const metrics = KIT_METRICS.reading;
  const [page, setPage] = useState(0);
  const [geometry, setGeometry] = useState<PageGeometry | null>(null);
  const count = layout === "pages" && geometry !== null ? geometry.count : 1;
  const shown = Math.min(page, count - 1);

  const measure = useCallback((): void => {
    const outer = textBox.current;
    const inner = lines.current;
    if (outer === null || inner === null) {
      return;
    }
    outer.style.height = "";
    if (layout !== "pages") {
      setGeometry(null);
      return;
    }
    const lineHeight = Number.parseFloat(getComputedStyle(outer).lineHeight);
    if (!(lineHeight > 0)) {
      return;
    }
    const next = pageGeometry(outer.getBoundingClientRect().height, inner.getBoundingClientRect().height, lineHeight);
    outer.style.height = `${next.lines * lineHeight}px`;
    setGeometry((held) => (held !== null && held.lines === next.lines && held.count === next.count && held.lineHeight === next.lineHeight ? held : next));
  }, [layout, textBox]);

  useLayoutEffect(() => {
    setPage(0);
    if (textBox.current !== null) {
      textBox.current.scrollTop = 0;
    }
  }, [serial, textBox]);

  useLayoutEffect(() => {
    measure();
    const element = box.current;
    if (element === null || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, text, serial, box]);

  const turnedFrom = useRef<Element | null>(null);

  /**
   * Turns to the page a key names, remembering what had focus so that a control disabled under it hands focus on.
   *
   * @param key The key, or the key a control stands for.
   */
  const turn = useCallback(
    (key: string): void => {
      turnedFrom.current = document.activeElement;
      setPage((held) => turnPage(key, Math.min(held, count - 1), count));
    },
    [count],
  );

  useEffect(() => {
    const was = turnedFrom.current;
    turnedFrom.current = null;
    if (was instanceof HTMLButtonElement && was.disabled && box.current?.contains(was) === true) {
      box.current.querySelector<HTMLElement>("[data-kit='reading-actions'] > .ot-button--primary")?.focus();
    }
  }, [shown, box]);

  useEffect(() => {
    if (navigate === undefined) {
      return;
    }
    navigate.current = (key: string): void => {
      const outer = textBox.current;
      if (layout === "pages") {
        turn(key);
      } else if (outer !== null) {
        outer.scrollTop = scrollFor(key, { scrollTop: outer.scrollTop, scrollHeight: outer.scrollHeight, clientHeight: outer.clientHeight, lineHeight: Number.parseFloat(getComputedStyle(outer).lineHeight) || 1 });
      }
    };
    return () => {
      navigate.current = null;
    };
  }, [navigate, layout, turn, textBox]);

  const paged = layout === "pages";
  const top = paged && geometry !== null ? pageTop(shown, geometry) : 0;
  return (
    <div ref={area} className="ot-reading-area" data-kit="reading-area">
      <div
        ref={box}
        role="dialog"
        aria-label={label}
        aria-describedby={textId}
        className="ot-window ot-ui-frame-window ot-reading"
        data-kit="reading"
        data-layout={layout}
        style={{ width: logical(metrics.width), height: logical(metrics.height), left: logical(place.left), top: logical(place.top), padding: logical(metrics.padding), gap: logical(metrics.gap) }}
      >
        <div ref={textBox} id={textId} className={paged ? "ot-reading__text ot-reading__text--pages" : "ot-reading__text ot-scroll"} data-kit="reading-text" tabIndex={paged ? undefined : 0}>
          <div ref={lines} className="ot-reading__lines" data-kit="reading-lines" style={top === 0 ? undefined : { transform: `translateY(${-top}px)` }}>
            {text}
          </div>
        </div>
        <div className="ot-reading__actions" data-kit="reading-actions">
          {count > 1 ? (
            <div className="ot-reading__pager" data-kit="reading-pager">
              <Button onClick={() => turn("PageUp")} disabled={shown === 0}>
                {words.back}
              </Button>
              <span className="ot-reading__page" data-kit="reading-page" aria-live="polite">
                {words.pageOf(shown + 1, count)}
              </span>
              <Button onClick={() => turn("PageDown")} disabled={shown === count - 1}>
                {words.next}
              </Button>
            </div>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            {words.close}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The words of the panel's controls, copy the page owns: Close, Back and Next, and the page counter's line.
 */
export interface ReadingWords {
  readonly close: string;
  readonly back: string;
  readonly next: string;
  /**
   * The page counter's line.
   *
   * @param page The page shown, from 1.
   * @param count The number of pages.
   * @returns The line.
   */
  readonly pageOf: (page: number, count: number) => string;
}

/**
 * The reading panel's props.
 */
export interface ReadingPanelProps {
  readonly text: string;
  readonly layout?: ReadingLayout;
  readonly serial?: number;
  readonly label: string;
  readonly words: ReadingWords;
  readonly onClose: () => void;
  readonly panel?: RefObject<HTMLDivElement | null>;
  readonly textRef?: RefObject<HTMLDivElement | null>;
  readonly navigate?: RefObject<((key: string) => void) | null>;
}
